import ms from "ms";
import React, { useCallback, useMemo, useRef, useState } from "react";
import styles from "./CommandModals.module.scss";
import classnames from "classnames";
import { observer } from "mobx-react-lite";
import { useDependencyInjector } from "@whatslly/commons/di";
import { LOADER_IMG } from "@whatslly/commons/constants";
import { OrgConfigsStore } from "../../../services/OrgConfigsStore";
import type { ISidecarPairingStore, PairingModalProps } from "../ISidecarPairingStore";
import { QRCodeSVG } from "qrcode.react";
import { ErrorBox } from "@whatslly/ui-components/ErrorBox";
import { IModalInstance } from "@whatslly/ui-components/modals/ModalProvider";
import { MessageModal } from "@whatslly/ui-components/modals/MessageModal";
import { useTranslation, Trans } from "react-i18next";
import { BaseModal } from "@whatslly/ui-components/modals";
import { Button } from "@whatslly/ui-components/v2/Button";
import { useAsyncEffect } from "use-async-effect";
import type { ISidecarsStore } from "../ISidecarsStore";
import { InjectSidecarsStore } from "../ISidecarsStore";
import { SvgV2 } from "@whatslly/ui-components/v2/BaseElements/SvgV2";
import { PhoneInput } from "@whatslly/ui-components/v2/BaseElements/PhoneInput";
import type { CreateSidecar } from "@whatslly/types/sidecars-server/Sidecar";
import { channelTypeOptions, channelTypeToFriendlyName, channelTypeToPhoneVerifyType, SIDECAR_PAIRING_ERROR, SIDECAR_STATUS } from "@whatslly/types/sidecars-server/Sidecar";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { getPrettyPhone, stripNumberStringToOnlyNumbers } from "@whatslly/commons/utils";
import { TextInput } from "@whatslly/ui-components/v2/BaseElements/TextInput";
import type { SidecarInitPairingMode } from "@whatslly/types/sidecars-server/SidecarUrlParams";
const useIdentification = (store: ISidecarPairingStore) => {
  const userStore = useDependencyInjector<IUserStore>(InjectUserStore);
  const token = new URLSearchParams(location.search).get("token");
  const identification = useMemo(
    () =>
      token
        ? { token: new URLSearchParams(location.search).get("token") ?? "" }
        : userStore.etoken && store.pairingSidecar?.success
          ? { etoken: userStore.etoken, orgId: store.pairingSidecar.sidecar.orgId }
          : { token: "" },
    [userStore.etoken, store.pairingSidecar, location.search, token]
  );

  return identification;
};

const Loader: React.FC<{ runnerChannel: Exclude<CreateSidecar["channelType"], undefined>; initialPairingMode: SidecarInitPairingMode }> = React.memo(
  ({ runnerChannel, initialPairingMode }) => {
    const { t } = useTranslation();
    const sentencesPrefix = useMemo(() => {
      if (initialPairingMode === "phoneNumber") return "sidecars.pairing_boot_sentences_connect_phone_number";
      return "sidecars.pairing_boot_sentences";
    }, [initialPairingMode]);

    const sentences = useMemo(
      () =>
        Array.from({ length: Number(t("sidecars.pairing_boot_sentences_length")) }, (_, i) =>
          t(`${sentencesPrefix}_${i}`, { channelName: channelTypeToFriendlyName[runnerChannel] })
        ),
      [runnerChannel, t, sentencesPrefix]
    );
    const [index, setIndex] = useState(0);
    const [elipses, setElipses] = useState(0);
    const [fade, setFade] = useState(true);
    useAsyncEffect(
      (isMounted) => {
        /* istanbul ignore next -- @preserve */ if (!isMounted()) return;
        setFade(true);
        setElipses(0);
        setTimeout(() => isMounted() && setFade(false), ms("500ms"));
        setTimeout(/* istanbul ignore next -- @preserve */ () => isMounted() && setFade(true), ms("14.5s"));
        setTimeout(/* istanbul ignore next -- @preserve */ () => isMounted() && setElipses(1), ms("1s"));
        setTimeout(/* istanbul ignore next -- @preserve */ () => isMounted() && setElipses(2), ms("2s"));
        setTimeout(/* istanbul ignore next -- @preserve */ () => isMounted() && setElipses(3), ms("3s"));
        setTimeout(/* istanbul ignore next -- @preserve */ () => isMounted() && setIndex((index) => (index + 1) % sentences.length), ms("15s"));
      },
      [index, sentences]
    );

    return (
      <div className={styles.loader} data-testid="pairing-loader">
        <div className={classnames(styles.loaderText, { [styles.fade]: fade })}>
          {sentences[index]}
          {".".repeat(elipses)}
        </div>
        <img alt="tuvis loader" src={LOADER_IMG} />
      </div>
    );
  }
);

const LoginWithQrCode: React.FC<{ store: ISidecarPairingStore; runnerChannel: Exclude<CreateSidecar["channelType"], undefined> }> = observer(
  ({ store, runnerChannel }) => {
    const { t } = useTranslation();
    if (
      !store.pairingSidecar?.success ||
      store.pairingSidecar.sidecar.status !== SIDECAR_STATUS.PAIRING ||
      (store.pairingSidecar.sidecar.state.mode !== "QR" && store.pairingSidecar.sidecar.state.mode !== "QR_AUTH_CODE")
    ) {
      return null;
    }
    const channelOptions = channelTypeOptions.find((option) => option.value === runnerChannel);
    const titleTransKey = store.pairingSidecar.sidecar.state.mode === "QR" ? "sidecars.scan_qr" : "sidecars.scan_qr_auth_code";
    const states = {
      QR: (
        <QRCodeSVG
          value={store.pairingSidecar.sidecar.state.code || ""}
          size={250}
          level="M"
          imageSettings={{
            src: "https://assets.whatslly.com/product/img/tuvis/tuvis-vis-128.png",
            x: undefined,
            y: undefined,
            height: 48,
            width: 48,
            excavate: true,
          }}
        />
      ),
      QR_AUTH_CODE: <div className={styles.qrAuthCode}>{store.pairingSidecar.sidecar.state.code}</div>,
    };

    const channelName = channelTypeToFriendlyName[runnerChannel];

    return (
      <div className={styles.qr}>
        <div className={styles.title}>
          {titleTransKey === "sidecars.scan_qr" ? (
            <Trans
              i18nKey="sidecars.scan_qr"
              values={{ channelName }}
              components={[<span style={{ fontWeight: "bold", textDecoration: "underline" }} />, <span style={{ fontWeight: "bold" }} />]}
            />
          ) : (
            t(titleTransKey, { channelName })
          )}
        </div>
        <div className={styles.loginWithQrCodeContent} data-testid="pairing-qrcode">
          {states[store.pairingSidecar.sidecar.state.mode]}
        </div>
        <div onClick={() => store.changeMode("setPhoneNumber")} data-testid="change-mode-to-phone">
          {channelOptions?.linkWithPhoneDisabled !== true && (
            <Button text={t("sidecars.alt_login.link_with_phone", { channelName })} borderRadius="default" />
          )}
        </div>
        {channelName.toLowerCase() === "whatsapp" ? (
          <div className={styles.qrInstructionsGroup}>
            <div className={styles.qrInstructionsBox}>
              <strong>iOS:</strong>
              <div>
                <Trans i18nKey="sidecars.scan_qr_instructions.whatsapp.ios" components={[<span style={{ fontWeight: "bold" }} />]} />
              </div>
            </div>
            <div className={styles.qrInstructionsBox}>
              <strong>Android:</strong>
              <div>
                <Trans i18nKey="sidecars.scan_qr_instructions.whatsapp.android" components={[<span style={{ fontWeight: "bold" }} />]} />
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.qrInstructions}>
            <Trans i18nKey={`sidecars.scan_qr_instructions.${channelName.toLowerCase()}`} components={[<span style={{ fontWeight: "bold" }} />]} />
          </div>
        )}
      </div>
    );
  }
);

const BackToQRButton: React.FC<{ sidecarId: string; store: ISidecarPairingStore }> = observer(({ sidecarId, store }) => {
  const sidecarsStore = useDependencyInjector<ISidecarsStore>(InjectSidecarsStore);
  const identification = useIdentification(store);
  const { t } = useTranslation();
  return (
    <Button
      text={t("sidecars.alt_login.link_with_qr")}
      data-testid="chagne-mode-to-qr"
      borderRadius="default"
      onClick={() => {
        if (store.changeModeToState === "phoneNumber") {
          sidecarsStore.runCommand(sidecarId || "", identification, { command: "changePairingSettings", settings: { mode: "QR" } });
        }
        store.changeMode("QR");
      }}
    />
  );
});

const AltLoginSetPhone: React.FC<{ sidecarId: string; store: ISidecarPairingStore }> = observer(({ sidecarId, store }) => {
  const sidecarsStore = useDependencyInjector<ISidecarsStore>(InjectSidecarsStore);
  const textInputRef = useRef<HTMLInputElement>(null);
  const identification = useIdentification(store);
  const handleCountryCodeChange = (code: string) => {
    store.setCountryCode(code);
    textInputRef.current?.focus();
  };
  const { t } = useTranslation();
  return (
    <>
      <div className={styles.loginWithPhoneContent}>
        <PhoneInput
          countryCode={store.phoneCountryCode}
          phoneNumberWithOutCountryCode={store.pairingPhone}
          onPhoneChange={(phoneNumberWithOutCountryCode) => store.setPairingPhone(phoneNumberWithOutCountryCode)}
          onCountryCodeChange={handleCountryCodeChange}
          testid={"phone-number-input"}
        />
      </div>
      <div className={styles.loginWitnPhoneButtons}>
        <Button
          text={t("sidecars.alt_login.get_code")}
          data-testid="get-code"
          borderRadius="default"
          onClick={() => {
            sidecarsStore.runCommand(sidecarId || "", identification, {
              command: "changePairingSettings",
              settings: { mode: "phoneNumber", phone: `${store.phoneCountryCode}${store.pairingPhone}` },
            });
            store.changeMode("phoneNumber");
          }}
          disabled={store.pairingPhone.length + store.phoneCountryCode.length < 8}
        />
        <BackToQRButton sidecarId={sidecarId} store={store} />
      </div>
    </>
  );
});

const VerifyPhoneNumber: React.FC<{
  sidecarId: string;
  store: ISidecarPairingStore;
}> = ({ store, sidecarId }) => {
  const { t } = useTranslation();
  const sidecarsStore = useDependencyInjector<ISidecarsStore>(InjectSidecarsStore);
  const identification = useIdentification(store);
  const [verifyCode, setVerifyCode] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const onVerifyCode = useCallback(async () => {
    setErrorMessage("");
    setIsSubmitting(true);
    const result = await sidecarsStore.runCommand(sidecarId || "", identification, {
      command: "sendLoginCode",
      code: verifyCode,
    });
    if (result && "errorMessage" in result && result.errorMessage === "PHONE_CODE_INVALID") {
      setErrorMessage(t("sidecars.alt_login.invalid_code"));
    }
    setIsSubmitting(false);
  }, [identification, sidecarId, sidecarsStore, t, verifyCode]);

  return (
    <div className={styles.verifyPhoneNumber}>
      <div className={styles.verifyPhoneNumberContainer}>
        <TextInput
          value={verifyCode}
          onChange={(e) => setVerifyCode(stripNumberStringToOnlyNumbers(e.target.value))}
          type={"number"}
          autoComplete={"off"}
          paddingInline={"none"}
          onClear={() => setVerifyCode("")}
          disabled={isSubmitting}
        />
        <Button
          isSubmitting={isSubmitting}
          disabled={!verifyCode || verifyCode.length !== 5}
          borderRadius="default"
          onClick={onVerifyCode}
          text={t("sidecars.alt_login.verify_phone_code")}
        />
      </div>
      {errorMessage && <div className={styles.verifyPhoneNumberError}>{errorMessage}</div>}
    </div>
  );
};

const CodeComponent: React.FC<{
  sidecarId: string;
  code?: string;
  store: ISidecarPairingStore;
  runnerChannel: Exclude<CreateSidecar["channelType"], undefined>;
}> = ({ runnerChannel, store, code, sidecarId }) => {
  const { t } = useTranslation();
  const sidecarsStore = useDependencyInjector<ISidecarsStore>(InjectSidecarsStore);
  const identification = useIdentification(store);
  const [verifyCode, setVerifyCode] = useState<string>("");

  if (channelTypeToPhoneVerifyType[runnerChannel] === "sendLoginCode") {
    return <VerifyPhoneNumber store={store} sidecarId={sidecarId} />;
  }
  if (!code) return <></>;
  return (
    <div className={styles.codeContainer} data-testid="pairing-code">
      {code.split("").map((letter, key) => (
        <React.Fragment key={`${letter}-${key}`}>
          {key === code.length / 2 && <div>-</div>}
          <div className={styles.codeLetter}>{letter}</div>
        </React.Fragment>
      ))}
    </div>
  );
};

const AltLoginCode: React.FC<{
  sidecarId: string;
  code?: string;
  store: ISidecarPairingStore;
  phone?: string;
  runnerChannel: Exclude<CreateSidecar["channelType"], undefined>;
}> = observer(({ sidecarId, code, store, phone, runnerChannel }) => {
  const { t } = useTranslation();
  return (
    <>
      <div className={styles.loginWithPhoneContent}>
        {phone && (
          <div>
            <div className={styles.pairingSteps}>
              <div>{t("sidecars.pairing_steps.first")}</div>
              <div className={styles.phoneTypeIcons}>
                {t("sidecars.pairing_steps.second.on_android")}
                <div className={styles.svgWrapper}>
                  <SvgV2 width={20} height={20} iconName="android-menu" />
                </div>
                {t("sidecars.pairing_steps.second.on_iphone")}
                <div className={styles.svgWrapper}>
                  <SvgV2 width={20} height={20} iconName="iphone-menu" />
                </div>
              </div>
              <div>{t("sidecars.pairing_steps.third")}</div>
              <div>{t("sidecars.pairing_steps.fourth")}</div>
              <div>{t("sidecars.pairing_steps.fifth")}</div>
            </div>
            <div className={styles.linkPhone}>
              <div>{t("sidecars.alt_login.link_phone", { channelName: channelTypeToFriendlyName[runnerChannel] })}</div>
              <div>{getPrettyPhone(phone)}</div>
            </div>
          </div>
        )}
        <CodeComponent code={code} store={store} runnerChannel={runnerChannel} sidecarId={sidecarId} />
      </div>
      <div className={styles.loginWitnPhoneButtons}>
        <Button
          text={t("sidecars.alt_login.edit_phone")}
          data-testid="edit-phone-button"
          borderRadius="default"
          onClick={() => {
            store.changeMode("setPhoneNumber");
          }}
        />
        <BackToQRButton sidecarId={sidecarId} store={store} />
      </div>
    </>
  );
});

const LoginWithAltLogin: React.FC<{
  code?: string;
  sidecarId: string;
  store: ISidecarPairingStore;
  phone?: string;
  runnerChannel: Exclude<CreateSidecar["channelType"], undefined>;
}> = observer(({ code, sidecarId, store, phone, runnerChannel }) => {
  const { t } = useTranslation();
  return (
    <div className={styles.loginWithPhoneWrapper}>
      <div className={styles.title}>
        {store.changeModeToState === "setPhoneNumber"
          ? t("sidecars.alt_login.enter_phone_number")
          : channelTypeToPhoneVerifyType[runnerChannel] === "sendLoginCode"
            ? t("sidecars.alt_login.send_login_code", { channelName: channelTypeToFriendlyName[runnerChannel] })
            : t("sidecars.alt_login.enter_code")}
      </div>
      <div className={styles.loginWithPhoneContainer}>
        {store.changeModeToState === "setPhoneNumber" ? (
          <AltLoginSetPhone sidecarId={sidecarId} store={store} />
        ) : (
          <AltLoginCode sidecarId={sidecarId} code={code} store={store} phone={phone} runnerChannel={runnerChannel} />
        )}
      </div>
    </div>
  );
});

const CONSIDER_AS_TIMEOUT = ms("15m");

type PairingErrorType = "timeout" | "phone_mismatch" | "generic";

const TIMEOUT_STEPS: Array<{ title: string; body: string }> = [
  { title: "sidecars.pairing_timeout_step_1_title", body: "sidecars.pairing_timeout_step_1_body" },
  { title: "sidecars.pairing_timeout_step_2_title", body: "sidecars.pairing_timeout_step_2_body" },
  { title: "sidecars.pairing_timeout_step_3_title", body: "sidecars.pairing_timeout_step_3_body" },
];

const PHONE_MISMATCH_STEPS: Array<{ title: string; body: string }> = [
  { title: "sidecars.pairing_phone_mismatch_step_1_title", body: "sidecars.pairing_phone_mismatch_step_1_body" },
  { title: "sidecars.pairing_phone_mismatch_step_2_title", body: "sidecars.pairing_phone_mismatch_step_2_body" },
];

const PairingErrorView: React.FC<{
  errorType: PairingErrorType;
  restartPairing: () => void;
  customErrorText?: string;
}> = ({ errorType, restartPairing, customErrorText }) => {
  const { t } = useTranslation();
  const bodyKey =
    errorType === "timeout"
      ? "sidecars.pairing_timeout"
      : errorType === "phone_mismatch"
        ? "sidecars.pairing_phone_mismatch"
        : "sidecars.pairing_error_generic";
  const steps = errorType === "timeout" ? TIMEOUT_STEPS : errorType === "phone_mismatch" ? PHONE_MISMATCH_STEPS : [];
  return (
    <div className={styles.pairingErrorContainer}>
      <div className={styles.pairingErrorIconCircle}>!</div>
      <h2 className={styles.pairingErrorTitle}>{t("sidecars.pairing_error_title")}</h2>
      <p className={styles.pairingErrorBody}>{t(bodyKey)}</p>
      {steps.length > 0 && (
        <div className={styles.pairingStepsSection}>
          <p className={styles.pairingStepsSectionTitle}>{t("sidecars.what_to_do_now")}</p>
          <ol className={styles.pairingSteps}>
            {steps.map((step, i) => (
              <li key={i}>
                <span className={styles.pairingStepTitle}>{t(step.title)}</span> {t(step.body)}
              </li>
            ))}
          </ol>
        </div>
      )}
      <Button onClick={restartPairing}>{t("sidecars.generate_new_qr_code")}</Button>
      {customErrorText && <div className={styles.remoteSyncCustomErrorText}>{customErrorText}</div>}
    </div>
  );
};

const PairingState: React.FC<{
  pairingStart: number;
  restartPairing: () => void;
  sidecarId: string;
  store: ISidecarPairingStore;
  runnerChannel: Exclude<CreateSidecar["channelType"], undefined>;
}> = observer(({ pairingStart, restartPairing, sidecarId, store, runnerChannel }) => {
  const orgConfigsStore = useDependencyInjector(OrgConfigsStore);
  const initialPairingMode = useMemo(() => store.initialPairingMode, [store.initialPairingMode]);
  if (!store.pairingSidecar) {
    return <Loader runnerChannel={runnerChannel} initialPairingMode={initialPairingMode} />;
  }
  if (!store.pairingSidecar.success) {
    return <ErrorBox error={store.pairingSidecar.error} />;
  }
  if (store.pairingSidecar.sidecar.status === SIDECAR_STATUS.ERROR) {
    const sidecarError = store.pairingSidecar.sidecar.state.error;
    const customErrorText = orgConfigsStore.getConfig("remote_sync_custom_error_text") || undefined;
    const isTimeout = pairingStart < Date.now() - CONSIDER_AS_TIMEOUT || sidecarError === SIDECAR_PAIRING_ERROR.OPERATION_ABORTED;
    const errorType: PairingErrorType =
      isTimeout || sidecarError === SIDECAR_PAIRING_ERROR.FAILED_TO_PAIR
        ? "timeout"
        : sidecarError === SIDECAR_PAIRING_ERROR.PHONE_MISMATCH
          ? "phone_mismatch"
          : "generic";
    return <PairingErrorView errorType={errorType} restartPairing={restartPairing} customErrorText={customErrorText} />;
  }

  if (store.pairingSidecar.sidecar.status === SIDECAR_STATUS.BOOTING) {
    return <Loader runnerChannel={runnerChannel} initialPairingMode={initialPairingMode} />;
  }
  if (store.pairingSidecar.sidecar.status === SIDECAR_STATUS.PAIRING) {
    if (store.changeModeRequest.state === "pending" && store.changeModeToState !== "setPhoneNumber")
      return <Loader runnerChannel={runnerChannel} initialPairingMode={initialPairingMode} />;
    if (
      (store.pairingSidecar.sidecar.state.mode === "QR" || store.pairingSidecar.sidecar.state.mode === "QR_AUTH_CODE") &&
      store.changeModeToState !== "setPhoneNumber"
    ) {
      return <LoginWithQrCode store={store} runnerChannel={runnerChannel} />;
    }

    return (
      <LoginWithAltLogin
        runnerChannel={runnerChannel}
        phone={store.pairingSidecar.sidecar.state.phone || ""}
        code={store.pairingSidecar.sidecar.state.code || ""}
        sidecarId={sidecarId}
        store={store}
      />
    );
  }

  return null;
});

const noop = () => {
  /* noop */
};

export const ChannelPairingModal: React.FC<PairingModalProps> = observer(
  ({ restartPairing, sidecarId, store, cancelable, closeModal, onClose, runnerChannel }) => {
    const pairingStart = useMemo(() => Date.now(), []);

    const handleClose = useCallback(() => {
      closeModal(false);
      onClose();
    }, [closeModal, onClose]);

    return (
      <BaseModal
        testId="sidecar-pairing-modal"
        closeModal={noop}
        className={classnames(styles.pairSidecarModal, {
          [styles.cancelable]: cancelable,
          [styles.qrModal]: !store.changeModeToState || store.changeModeToState === "QR",
        })}
        bodyClassName={styles.qrModalBody}
        header={cancelable && <SvgV2 iconName="cancel-actions" height={12} width={12} className={styles.cancelModalButton} onClick={handleClose} />}
      >
        <PairingState pairingStart={pairingStart} restartPairing={restartPairing} sidecarId={sidecarId} store={store} runnerChannel={runnerChannel} />
      </BaseModal>
    );
  }
);

export const ErrorModal: React.FC<IModalInstance & { error: string }> = observer(({ closeModal, error }) => {
  return (
    <MessageModal closeModal={closeModal}>
      <ErrorBox error={error} />
    </MessageModal>
  );
});
